/**
 * DashboardHome — Main dashboard "Mission Control" view.
 *
 * Grid layout: metric cards row, sandbox overview, activity feed, quick actions.
 * Fetches dashboard stats on mount via useDashboardStore proxy through api.
 */
import { useEffect, useState } from "react";
import {
  Activity,
  Zap,
  MessageSquare,
  Radio,
} from "lucide-react";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { ActivityFeed } from "@/components/dashboard/ActivityFeed";
import { SandboxOverview } from "@/components/dashboard/SandboxOverview";
import { QuickActions } from "@/components/dashboard/QuickActions";
import { api } from "@/lib/api";
import type { DashboardStats } from "@ajnt/shared";

export function DashboardHome() {
  const [stats, setStats] = useState<DashboardStats | null>(null);

  useEffect(() => {
    async function fetchStats() {
      try {
        const data = await api.get<DashboardStats>("/api/dashboard/stats");
        setStats(data);
      } catch {
        // Stats will remain null — cards show "--"
      }
    }
    fetchStats();
  }, []);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Mission Control</h1>
        <p className="text-sm text-muted-foreground">
          Overview of your workspace activity
        </p>
      </div>

      {/* Metric cards row */}
      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        data-testid="metric-cards"
      >
        <MetricCard
          icon={<Activity className="h-5 w-5" />}
          label="Active Sessions"
          value={stats?.activeSessions ?? "--"}
          trend="neutral"
        />
        <MetricCard
          icon={<Zap className="h-5 w-5" />}
          label="Total Actions"
          value={stats?.totalActions ?? "--"}
          trend="up"
          trendLabel="today"
        />
        <MetricCard
          icon={<MessageSquare className="h-5 w-5" />}
          label="Messages"
          value={stats?.totalMessages ?? "--"}
          trend="up"
        />
        <MetricCard
          icon={<Radio className="h-5 w-5" />}
          label="Active Channels"
          value={stats?.activeChannels ?? "--"}
          trend="neutral"
        />
      </div>

      {/* Main grid: sandbox + activity + actions */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 flex flex-col gap-6">
          <SandboxOverview />
          <ActivityFeed />
        </div>
        <div>
          <QuickActions />
        </div>
      </div>
    </div>
  );
}
